import { Box, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { useNavigate } from 'react-router-dom';
import { reducerPaths } from '../../../app/reducerPaths';
import { setAlertDialog } from '../../../toolkit/alertDialog/alertDialogSlice';
import { useLazyVehicleOfferPriceQuery } from '../../../toolkit/carDetails/carDetailsApi';
import { useLazyGetBidHistoryQuery } from '../../../toolkit/liveAuction/liveAuctionApi';
import {
  setActiveStep,
  setGalleryForm,
  setGeneralInfoForm,
  setMyGarageMultipleFields,
  setQuestionareForm,
  setSpecificationsForm,
} from '../../../toolkit/myGarage/myGarageSlice';
import { useSetVehicleVisitMutation } from '../../../toolkit/vehicle/vehicleApi';
import { carData } from '../../../utils/constants/carData';
import { MyGarageSteps, pageTabs } from '../../../utils/constants/myGarage';
import { routes } from '../../../utils/constants/routes';
import { isVehicleEditButtonEnabled } from '../../../utils/methods/generalVehicleMethods';
import {
  getGeneralInfo,
  getQuestionareHandler,
  setSellMyCarFormsHandler,
} from '../../../utils/methods/setSellMyCarForms';
import { BidHistory } from './BidHistory';
import CarQuestionsSection from './CarQuestionsSection';
import CarRelistSection from './CarRelistSection';
import VehicleFooterInfo from './VehicleFooterInfo';
import VehicleInfoSection from './VehicleInformation';
import { CarCarousel } from './common/CarCarousel';

const CarDetails = ({ lg = 9, car = {}, showEdit = false }) => {
  // usetStates start
  const [tabValue, setTabValue] = useState(0);
  // useStates end
  const navigate = useNavigate();
  const auth = useSelector((store) => store[reducerPaths.auth]);
  const myGarage = useSelector((store) => store[reducerPaths.myGarage]);
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // custom hooks start...
  const [getBidHistoryApiHandler, getBidHistoryApiData] = useLazyGetBidHistoryQuery();
  const [getVehicleOfferPriceApiHandler, { data: vehicleOfferData }] = useLazyVehicleOfferPriceQuery();
  const [setVehicleVisitApiHandler, setVehicleVisitApiData] = useSetVehicleVisitMutation();
  // const { data: vehicleOfferData } = useVehicleOfferPriceQuery({
  //   storedVehicleId: car?.Vehicle_Id,
  //   vin: car?.VIN,
  //   mileage: car?.Mileage,
  //   trim: car?.Trim,
  //   year: car?.Year,
  //   make: car?.Make,
  //   model: car?.Model,
  // });
  const priceData = useMemo(() => {
    return [
      {
        label: 'Low',
        value: vehicleOfferData?.second_price,
      },
      { label: 'Average', value: vehicleOfferData?.third_price },
      { label: 'Awesome', value: vehicleOfferData?.first_price },
    ];
  }, [vehicleOfferData]);
  // custom hooks end...
  // Handlers start...
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getVehicleInfoHandler = (car) => {
    const { accessories, ...rest } = getGeneralInfo({
      data: {
        vehicle: car,
      },
    });
    return {
      ...rest,
      Notes: car?.Car_Notes,
      Relist_Count: car?.Relist_Count,
    };
  };

  const getQuestionareInfoHandler = (car) => {
    const data = getQuestionareHandler({
      data: {
        vehicle: car,
      },
    });

    return carData.questions?.map((question) => {
      return {
        ...question,
        answer: [question?.fieldName] in data ? (data?.[question?.fieldName] ? 'Yes' : 'No') : question?.answer,
      };
    });
  };
  // Handlers end...

  // useEffect start...
  React.useEffect(() => {
    if (!car?.Vehicle_Id) {
      return;
    }

    (async () => {
      await getBidHistoryApiHandler({
        vehicleId: car?.Vehicle_Id,
      });
    })();
  }, [car?.Vehicle_Id]);

  React.useEffect(() => {
    const { Vehicle_Id, VIN, Mileage, Trim, Year, Make, Model } = car;
    if (Vehicle_Id && VIN && Mileage && Trim && Year && Make && Model) {
      (async () => {
        await getVehicleOfferPriceApiHandler({
          storedVehicleId: car?.Vehicle_Id,
          params: {
            vin: car?.VIN,
            mileage: car?.Mileage,
            trim: car?.Trim,
            year: car?.Year,
            make: car?.Make,
            model: car?.Model,
          }
        });
      })();
    }
  }, [car?.Vehicle_Id]);

  React.useEffect(() => {
    (async () => {
      const res = await setVehicleVisitApiHandler({
        payload: {
          vehicle_id: car?.Vehicle_Id,
        },
      });
    })();
  }, [car?.Vehicle_Id]);
  // useEffect end...

  console.log(car?.images, "CarCarousel")

  return (
    <>
      {/* Image carousel */}
      {car?.images?.length > 0 || car?.Vehicle_video ? (
        <CarCarousel
          images={car?.images}
          Vehicle_Id={car?.Vehicle_Id}
          Vehicle_video={car?.Vehicle_video}
          vehicleData={car}
        />
      ) : (
        <Typography fontWeight={700}>No Images Available</Typography>
      )}
      {/* Mobile tabs navigation */}
      {isMobile && (
        <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth" sx={{ mb: 3 }}>
          <Tab label={<Typography>Details</Typography>} />
          <Tab label={<Typography>Bid History</Typography>} />
          <Tab label={<Typography>Questions</Typography>} />
        </Tabs>
      )}

      {/* Car details content - conditionally rendered on mobile */}
      {!isMobile || tabValue === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
          }}
          position={'relative'}
        >
          <VehicleInfoSection
            vehicleData={{
              ...car,
              ...getVehicleInfoHandler(car),
            }}
            // showEdit={auth?.user_id === car?.Seller_Id || showEdit}
            showEdit={isVehicleEditButtonEnabled({
              car,
            })}
            onClickEdit={() => {
              if (!car?.Vehicle_Id) {
                dispatch(
                  setAlertDialog({
                    open: true,
                    title: `Error!`,
                    message: `Vehicle id is not available. Please reload the page and try again!`,
                  })
                );

                return;
              }
              setSellMyCarFormsHandler({
                dispatch,
                data: {
                  vehicle: car,
                },
                setGeneralInfoForm: setGeneralInfoForm,
                setQuestionareForm: setQuestionareForm,
                setSpecificationsForm: setSpecificationsForm,
                setGalleryForm: setGalleryForm,
                forms: myGarage?.forms,
                previouslySubmittedForms: myGarage?.previouslySubmittedForms,
              });
              dispatch(
                setMyGarageMultipleFields({
                  isEditing: true,
                  areEditFormsSet: true,
                  vehicle_id: car?.Vehicle_Id,
                })
              );
              dispatch(setActiveStep(MyGarageSteps[0]));
              navigate(`${routes.dashboardNavigation}${routes.myGarage}`, {
                state: { pageTab: pageTabs.sellNewCar },
              });
            }}
          />
          <CarRelistSection priceData={priceData} />
        </Box>
      ) : null}
      {/* Bid history - conditionally rendered on mobile */}
      {(!isMobile || tabValue === 1) && isMobile && <BidHistory bids={car?.bidHistory} />}
      {/* Questions section - conditionally rendered on mobile */}
      {(!isMobile || tabValue === 2) && <CarQuestionsSection questions={getQuestionareInfoHandler(car)} />}
      <VehicleFooterInfo />
    </>
  );
};

export default CarDetails;
